import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface JSXErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface JSXErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export class JSXErrorBoundary extends React.Component<
  JSXErrorBoundaryProps,
  JSXErrorBoundaryState
> {
  constructor(props: JSXErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): JSXErrorBoundaryState {
    console.error('🚨 JSX Error Boundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 JSX Runtime Error:', error);
    console.error('🚨 JSX Error Info:', errorInfo);
    
    // Auto-reload on stale chunk / dynamic import errors
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('error loading dynamically imported module')
    ) {
      console.warn('🔄 Stale chunk detected, reloading page...');
      // Prevent infinite reload loop
      const lastReload = sessionStorage.getItem('chunk-reload');
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem('chunk-reload', String(now));
        window.location.reload();
        return;
      }
    }

    // Log specific JSX errors
    if (error.message.includes('jsxDEV') || error.message.includes('createElement')) {
      console.error('🚨 JSX Transform Error detected - this is likely a build configuration issue');
    }

    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="max-w-md w-full">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="mt-2">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Application Error</h3>
                    <p className="text-sm">
                      The app encountered a JSX rendering error. This is usually caused by build configuration issues.
                    </p>
                  </div>
                  
                  {this.state.error && (
                    <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded text-xs">
                      <strong>Error:</strong> {this.state.error.message}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.reload()}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reload Page
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => this.setState({ hasError: false })}
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}